import { Spin, Tabs } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroll-component'
import Masonry from 'react-masonry-css'

import { useGetFeaturedPostsQuery, useGetTopFeaturedPostsQuery, useGetTrendingPostsQuery } from '@/generated/graphql'
import { PostCard } from '@/modules/post/post-card'

import s from './trending.module.less'

const { TabPane } = Tabs
interface rankListProps {
  timeSpan: number
}

export const TrendingPostList: React.FC<rankListProps> = ({ timeSpan }) => {
  const fetchCount = 15
  const { data, error, loading, fetchMore } = useGetTrendingPostsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      timeSpan: timeSpan,
      pagination: {
        last: fetchCount,
      },
    },
  })

  if (error) {
    return null
  }

  const pageInfo = data
    ? data.recommend.trendingPosts.pageInfo
    : {
        startCursor: null,
        endCursor: null,
        hasPreviousPage: false,
        hasNextPage: false,
      }

  const posts = data
    ? data.recommend.trendingPosts.edges
        .map(({ node }) => node)
        .slice()
        .reverse()
    : []

  const postTrending = posts.map((p) => (
    <PostCard key={p.postId} post={p} disableActionText={true} isRecommended={true} />
  ))

  const loadingComponent = loading && (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <Spin size='large' />
    </div>
  )
  return (
    <div>
      <InfiniteScroll
        dataLength={postTrending.length}
        loader={loadingComponent}
        hasMore={pageInfo.hasPreviousPage}
        next={() => {
          fetchMore({
            variables: {
              pagination: {
                last: fetchCount,
                before: pageInfo.startCursor,
              },
            },
          })
        }}
      >
        <Masonry
          className={s.masonry}
          columnClassName={s.masonryColumn}
          breakpointCols={{
            default: 3,
            1500: 2,
            1000: 1,
          }}
        >
          {postTrending}
        </Masonry>
      </InfiniteScroll>
    </div>
  )
}

export const TrendingPost = () => {
  const { t } = useTranslation()
  const [tab, setTab] = React.useState<string>('week')

  const timespanHeader = (
    <div className={s.timeSpan}>
      <Tabs animated={false} activeKey={tab} size={'small'} tabBarGutter={8} onChange={(tab) => setTab(tab)}>
        <TabPane tab={t('filter.week')} key='week' />
        <TabPane tab={t('filter.month')} key='month' />
        <TabPane tab={t('filter.season')} key='season' />
        <TabPane tab={t('filter.year')} key='year' />
      </Tabs>
    </div>
  )

  const timespan = {
    week: 7,
    month: 30,
    season: 90,
    year: 365,
  }[tab]

  const contents = (() => {
    return (
      <div className={s.trendPost}>
        <TrendingPostList timeSpan={timespan!} />
      </div>
    )
  })()

  return (
    <>
      {timespanHeader}
      {contents}
    </>
  )
}

export const FeaturedPosts = () => {
  const fetchCount = 10
  const { data, error, loading, fetchMore } = useGetFeaturedPostsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      pagination: {
        last: fetchCount,
      },
    },
  })

  const { data: top, error: topError } = useGetTopFeaturedPostsQuery()

  if (error || topError) {
    return null
  }

  const topFeatured = top?.recommend.topFeaturedPosts

  const pageInfo = data
    ? data.recommend.featuredPosts.pageInfo
    : {
        startCursor: null,
        endCursor: null,
        hasPreviousPage: false,
        hasNextPage: false,
      }

  const posts = data
    ? data.recommend.featuredPosts.edges
        .map(({ node }) => node)
        .slice()
        .reverse()
    : []

  const loadingComponent = loading && (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <Spin size='large' />
    </div>
  )
  return (
    <div>
      <InfiniteScroll
        dataLength={posts.length + (topFeatured ? 1 : 0)}
        loader={loadingComponent}
        className={s.featured}
        initialLoad={false}
        hasMore={pageInfo.hasPreviousPage}
        useWindow={true}
        next={() => {
          fetchMore({
            variables: {
              pagination: {
                last: fetchCount,
                before: pageInfo.startCursor,
              },
            },
          })
        }}
      >
        {topFeatured && <PostCard post={topFeatured} />}
        {posts.map((p) => (
          <PostCard key={p.postId} post={p} />
        ))}
      </InfiniteScroll>
    </div>
  )
}
